import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Upload, FileText, Image } from 'lucide-react';
import { toast } from './ui/use-toast';

export const ICPDescriptionUpload: React.FC = () => {
  const [description, setDescription] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file: File) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF or image file.",
        variant: "destructive"
      });
      return;
    }

    setUploadedFile(file);
    toast({
      title: "File uploaded",
      description: `${file.name} has been uploaded successfully.`,
    });
  };

  const handleSubmit = () => {
    if (!description.trim() && !uploadedFile) {
      toast({
        title: "No input provided",
        description: "Please provide either a description or upload a file.",
        variant: "destructive"
      });
      return;
    }

    // TODO: Process the ICP description with OpenAI
    console.log('Processing ICP:', { description, file: uploadedFile });
    toast({
      title: "Processing ICP",
      description: "Analyzing your ICP description...",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="description" className="text-white mb-2 block">
          Describe your Ideal Customer Profile
        </Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your ideal customer profile in detail. Include demographics, company size, industry, pain points, buying behavior, etc."
          rows={6}
          className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-purple-500"
        />
      </div>

      <div className="text-center text-slate-400">
        <span>OR</span>
      </div>

      <div>
        <Label className="text-white mb-2 block">
          Upload Document (PDF or Image)
        </Label>
        <Card 
          className={`border-2 border-dashed transition-colors cursor-pointer ${
            dragActive 
              ? 'border-purple-500 bg-purple-500/10' 
              : 'border-slate-600 bg-slate-700/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <CardContent className="p-8 text-center">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.gif"
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="flex flex-col items-center gap-4">
                {uploadedFile ? (
                  <>
                    <div className="p-4 bg-green-600/20 rounded-full">
                      {uploadedFile.type.startsWith('image/') ? (
                        <Image className="w-8 h-8 text-green-400" />
                      ) : (
                        <FileText className="w-8 h-8 text-green-400" />
                      )}
                    </div>
                    <div className="text-white">
                      <p className="font-medium">{uploadedFile.name}</p>
                      <p className="text-sm text-slate-400">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 bg-slate-600 rounded-full">
                      <Upload className="w-8 h-8 text-slate-300" />
                    </div>
                    <div className="text-slate-300">
                      <p className="font-medium">Drop files here or click to upload</p>
                      <p className="text-sm text-slate-400">
                        Supports PDF, JPG, PNG, GIF
                      </p>
                    </div>
                  </>
                )}
              </div>
            </label>
          </CardContent>
        </Card>
      </div>

      <Button 
        onClick={handleSubmit}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0"
        disabled={!description.trim() && !uploadedFile}
      >
        Analyze ICP
      </Button>
    </div>
  );
}; 